import React, { createContext, useContext, useEffect, useCallback, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useVerification } from "../hooks/useVerification";
import { refreshVerificationData as refreshUtil } from "../utils/verificationUtils";

interface VerificationContextType {
    refreshVerificationData: () => Promise<void>;
    isRefreshing: boolean;
    lastRefreshTime: number | null;
}

const VerificationContext = createContext<VerificationContextType | undefined>(undefined);

interface VerificationProviderProps {
    children: ReactNode;
}

export const VerificationProvider: React.FC<VerificationProviderProps> = ({ 
    children
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state: any) => state.auth);
    const verificationData = useSelector((state: any) => state.client.verificationData);
    const [isRefreshing, setIsRefreshing] = React.useState(false);
    const [lastRefreshTime, setLastRefreshTime] = React.useState<number | null>(null);
    const [hasInitialized, setHasInitialized] = React.useState(false);

    const refreshVerificationData = useCallback(async (force: boolean = false) => {
        if (!user?.id || isRefreshing) return;
        
        // Handle both string and number user IDs
        let userId: number | string = user.id;
        
        // If it's a string, try to convert to number, but keep as string if it's not a valid number
        if (typeof userId === "string") {
            const numericId = Number(userId);
            if (!isNaN(numericId) && numericId > 0) {
                userId = numericId;
            }
            // If it's a string that can't be converted to a valid number, keep it as string
        }
        
        if (!userId) {
            console.warn("Invalid user ID for verification refresh:", user.id);
            return;
        }
        
        try {
            setIsRefreshing(true);
            await refreshUtil(userId, force);
            setLastRefreshTime(Date.now());
        } catch (error) {
            console.error("Failed to refresh verification data:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [user?.id]);

    // Reset initialization state when user changes
    useEffect(() => {
        setHasInitialized(false);
    }, [user?.id]);

    // Auto-refresh on mount and when user changes, but only if we don't have data
    useEffect(() => {
        if (user?.id && !hasInitialized) {
            refreshVerificationData();
            setHasInitialized(true);
        }
    }, [user?.id, hasInitialized, refreshVerificationData]);

    const value: VerificationContextType = {
        refreshVerificationData,
        isRefreshing,
        lastRefreshTime,
    };

    return (
        <VerificationContext.Provider value={value}>
            {children}
        </VerificationContext.Provider>
    );
};

export const useVerificationContext = (): VerificationContextType => {
    const context = useContext(VerificationContext);
    if (context === undefined) {
        throw new Error("useVerificationContext must be used within a VerificationProvider");
    }
    return context;
};

export default VerificationContext;
